import React, { useState, useRef, useEffect } from "react";
import "./styles.css";

interface DesignNameInputProps {
  initialValue: string;
  onSave?: (filename: string) => void;
  className?: string;
}

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M4 13L9 18L20 7" stroke="#0C172C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DesignNameInput: React.FC<DesignNameInputProps> = ({ initialValue, onSave, className }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState("Untitled Design");
  // Width snapshot taken at click time. The hover state shows the
  // border + "Rename" label + value, which gives the container a
  // specific width; switching to editing rebuilds the content
  // (input + ✓ button) and the `inline-flex` container would jump
  // to a different intrinsic width otherwise. Locking the width at
  // click time keeps the box visually identical between hover and
  // editing. Reset to `undefined` when editing ends so the
  // container goes back to natural sizing.
  const [lockedWidth, setLockedWidth] = useState<number | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    setLockedWidth(undefined);
    const trimmed = value.trim() || "Untitled Design";
    setValue(trimmed);
    if (onSave && trimmed !== initialValue) {
      onSave(trimmed);
    }
  };

  const handleClick = () => {
    if (!isEditing) {
      // Snapshot the hover-rendered width (border + "Rename" +
      // value) BEFORE we flip into editing, so the new content
      // (input + ✓) renders inside a fixed-width box that matches
      // what the user just saw on hover.
      if (containerRef.current) {
        setLockedWidth(containerRef.current.offsetWidth);
      }
      setIsEditing(true);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Don't close if clicking the confirm button (it's inside the container)
    if (containerRef.current && containerRef.current.contains(e.relatedTarget as Node)) {
      return;
    }
    handleSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setValue(initialValue);
      setIsEditing(false);
      setLockedWidth(undefined);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleConfirmClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSave();
  };

  if (!initialValue) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={`design-name-container ${isEditing ? "is-editing" : ""} ${className || ""}`}
      onClick={handleClick}
      // Apply the snapshot only while editing — once we exit, the
      // container goes back to its natural intrinsic width so a
      // longer / shorter saved value re-flows correctly.
      style={isEditing && lockedWidth !== undefined ? { width: `${lockedWidth}px` } : undefined}
    >
      {!isEditing && <span className="design-name-rename-label">Rename</span>}
      {isEditing ? (
        <>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            maxLength={50}
            className="design-name-input-field"
          />
          <button type="button" className="design-name-confirm-btn" onMouseDown={handleConfirmClick} tabIndex={-1}>
            <CheckIcon />
          </button>
        </>
      ) : (
        <span className="design-name-value">{value}</span>
      )}
    </div>
  );
};

export default DesignNameInput;
